import { Reveal } from "@/components/Reveal";
import { CUSTOM, HERO } from "@/constants/testIds";
import { UploadCloud, FileBox, Check, Store, DollarSign, Printer, Users } from "lucide-react";

const SELLER_POINTS = [
  { icon: DollarSign, title: "A money-making side gig", desc: "Earn on every sale and set your own prices with the built-in price calculator." },
  { icon: Printer, title: "Use the printer you own", desc: "Fulfill orders on your own machine, on your own schedule \u2014 no shop required." },
  { icon: Users, title: "We bring the buyers", desc: "List a design once and get discovered by shoppers browsing the marketplace." },
];

export const CustomPrints = ({ onScrollTo }) => {
  return (
    <section
      id="custom"
      data-testid={CUSTOM.section}
      className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
    >
      <div className="grid items-center gap-10 lg:grid-cols-2">
        {/* Left: copy for makers / sellers */}
        <div>
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(212,0,255,0.4)] bg-[rgba(212,0,255,0.1)] px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-neo-magenta">
              For makers &amp; sellers
            </span>
            <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              Turn your 3D printer into a side gig.
            </h2>
            <p className="mt-3 max-w-md text-base text-muted-foreground">
              Already have a printer? List your designs on Printopia, reach
              buyers everywhere, and get paid for every sale. You print and ship
              &mdash; we handle discovery, payments and shipping labels.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 space-y-4">
              {SELLER_POINTS.map((p) => (
                <div key={p.title} className="flex gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-secondary/50">
                    <p.icon className="h-4 w-4 text-neo-magenta" />
                  </div>
                  <div>
                    <div className="font-display text-sm font-semibold text-foreground">{p.title}</div>
                    <div className="text-sm text-muted-foreground">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid={CUSTOM.sellCta}
                onClick={() => onScrollTo?.("download")}
                className="inline-flex h-12 items-center gap-2 rounded-[var(--radius)] border border-[rgba(212,0,255,0.4)] bg-[rgba(212,0,255,0.14)] px-5 text-base font-medium text-foreground shadow-[var(--glow-magenta)] transition-[background-color,transform] duration-200 hover:bg-[rgba(212,0,255,0.2)] active:scale-[0.98]"
              >
                <Store className="h-4 w-4" /> Become a seller
              </button>
              <button
                data-testid={CUSTOM.uploadCta}
                onClick={() => onScrollTo?.("download")}
                className="inline-flex h-12 items-center gap-2 rounded-[var(--radius)] border border-border/70 bg-secondary/60 px-5 text-base font-medium text-foreground transition-[border-color,box-shadow] duration-200 hover:border-[rgba(0,229,255,0.4)] hover:shadow-[var(--glow-cyan)]"
              >
                <UploadCloud className="h-4 w-4" /> List a design
              </button>
            </div>
          </Reveal>
        </div>

        {/* Right: "List a design" upload widget (represents listing your model) */}
        <Reveal delay={0.1} x={20} y={0}>
          <div className="relative mx-auto max-w-md">
            <div
              data-testid={HERO.widget}
              className="relative z-10 overflow-hidden rounded-3xl border border-border/70 bg-card/80 p-6 shadow-[var(--shadow-elev-2)] backdrop-blur-md"
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-full overflow-hidden">
                <div className="animate-scanline h-10 w-full bg-gradient-to-b from-[rgba(212,0,255,0.22)] to-transparent" />
              </div>

              <div className="flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  List a design
                </span>
                <span className="rounded-full border border-[rgba(212,0,255,0.4)] px-2 py-0.5 font-mono text-[10px] text-neo-magenta">
                  DRAFT
                </span>
              </div>

              <div className="relative mt-4 rounded-2xl border-2 border-dashed border-border/70 bg-background/40 p-8 text-center">
                <div className="absolute left-3 top-3 h-4 w-4 border-l-2 border-t-2 border-[var(--neo-magenta)]" />
                <div className="absolute right-3 top-3 h-4 w-4 border-r-2 border-t-2 border-[var(--neo-magenta)]" />
                <div className="absolute bottom-3 left-3 h-4 w-4 border-b-2 border-l-2 border-[var(--neo-magenta)]" />
                <div className="absolute bottom-3 right-3 h-4 w-4 border-b-2 border-r-2 border-[var(--neo-magenta)]" />
                <UploadCloud className="mx-auto h-9 w-9 text-neo-magenta" />
                <p className="mt-3 text-sm font-medium text-foreground">
                  Upload your 3D model to list it
                </p>
                <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                  STL &bull; OBJ &bull; 3MF &bull; STEP
                </p>
              </div>

              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between rounded-lg border border-border/70 bg-secondary/50 px-3 py-2">
                  <span className="flex items-center gap-2 font-mono text-xs text-foreground">
                    <FileBox className="h-4 w-4 text-neo-cyan" /> my_design.stl
                  </span>
                  <span className="flex items-center gap-1 font-mono text-[11px] text-neo-magenta">
                    <Check className="h-3.5 w-3.5" /> ready
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border/70 bg-secondary/50 px-3 py-2">
                  <span className="font-mono text-xs text-muted-foreground">List price</span>
                  <span className="font-mono text-xs font-semibold text-foreground">$45.00</span>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.08)] px-3 py-2">
                  <span className="font-mono text-xs text-neo-cyan">You earn / sale</span>
                  <span className="font-mono text-xs font-semibold text-foreground">$41.42</span>
                </div>
              </div>

              <button
                type="button"
                disabled
                className="mt-5 w-full rounded-[var(--radius)] border border-[rgba(212,0,255,0.4)] bg-[rgba(212,0,255,0.16)] py-3 text-sm font-medium text-foreground shadow-[var(--glow-magenta)]"
              >
                Publish listing
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
